import { ChangeDetectionStrategy, Component, OnInit, input, model } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

let nextId = 0;

/**
 * A titled section whose body the user can fold away. The body stays in the
 * DOM while folded, so components inside it keep their state. With a
 * `headingLevel` the toggle sits inside a real heading (h2–h4), so the section
 * shows in the page's heading outline; the body is a region named after it.
 */
@Component({
  selector: 'app-collapsible-section',
  templateUrl: './collapsible-section.component.html',
  styleUrl: './collapsible-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
})
export class CollapsibleSectionComponent implements OnInit {
  readonly heading = input.required<string>();
  /** When set, the user's open/closed choice is remembered in this browser under this key. */
  readonly storageKey = input<string | undefined>(undefined);
  readonly open = model(false);
  /** Wraps the toggle in an h2–h4; null keeps a bare toggle (inside an existing heading structure). */
  readonly headingLevel = input<2 | 3 | 4 | null>(null);

  private readonly instance = nextId++;
  readonly bodyId = `collapsible-section-${this.instance}`;
  readonly toggleId = `collapsible-section-${this.instance}-toggle`;

  ngOnInit(): void {
    const key = this.storageKey();
    if (!key) return;
    const saved = readSaved(key);
    if (saved !== null) this.open.set(saved);
  }

  /** A user toggle; only this is remembered, never an owner opening it. */
  toggle(): void {
    const next = !this.open();
    this.open.set(next);
    const key = this.storageKey();
    if (key) save(key, next);
  }
}

function readSaved(key: string): boolean | null {
  try {
    const value = localStorage.getItem(key);
    return value === 'open' ? true : value === 'closed' ? false : null;
  } catch {
    return null;
  }
}

function save(key: string, open: boolean): void {
  try {
    localStorage.setItem(key, open ? 'open' : 'closed');
  } catch {
    /* storage unavailable: the section still works, it just is not remembered */
  }
}
